import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'primereact/button';
import UserTrafficSourceAuditSearch from './UserTrafficSourceAuditSearch';
import UserTrafficSourceAuditDataTable from './UserTrafficSourceAuditDataTable';
import UserTrafficSourceAuditAddDialog from './UserTrafficSourceAuditAddDialog';
import UserTrafficSourceAuditService from '../../services/UserTrafficSourceAudit/user-traffic-source-audit.service';
import {
  UserTrafficSourceAuditModel,
  UserTrafficSourceAuditFilterModel
} from '../../models/UserTrafficSourceAudit/user-traffic-source-audit-model';

const UserTrafficSourceAuditMain: React.FC = () => {
  const [isLoading, setIsLoading]         = useState(false);
  const [data, setData]                   = useState<UserTrafficSourceAuditModel[]>([]);
  const [pageNumber, setPageNumber]       = useState(1);
  const [pageSize, setPageSize]           = useState(10);
  const [totalRecords, setTotalRecords]   = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [sortField, setSortField]         = useState<string>('');
  const [sortOrder, setSortOrder]         = useState<1 | -1 | null>(null);
  const [filters, setFilters]             = useState<UserTrafficSourceAuditFilterModel>({
    pageNumber: 1,
    pageSize: 10
  });

  const fetchData = useCallback(async (overrideFilters?: UserTrafficSourceAuditFilterModel) => {
    setIsLoading(true);
    try {
      const payload = {
        ...(overrideFilters ?? filters),
        pageNumber,
        pageSize,
        sortBy: sortField || undefined,
        sortDirection: sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : undefined
      };
      const response = await UserTrafficSourceAuditService.getFiltered(payload);
      setData(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching audit data:', error);
      setData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pageNumber, pageSize, sortField, sortOrder]);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, filters, sortField, sortOrder]);

  const handleSearch = useCallback((newFilters: UserTrafficSourceAuditFilterModel) => {
    setFilters(newFilters);
    setPageNumber(1);
  }, []);

  const handleReset = useCallback(() => {
    const reset: UserTrafficSourceAuditFilterModel = { pageNumber: 1, pageSize: 10 };
    setFilters(reset);
    setSortField('');
    setSortOrder(null);
    setPageNumber(1);
  }, []);

  const handlePageChange = useCallback((event: any) => {
    setPageNumber(event.page + 1);
    setPageSize(event.rows);
  }, []);

  const handleRefresh = useCallback(() => fetchData(), [fetchData]);

  return (
    <div className="layout-content mt-3 mb-3">
      <style>{`
        .complaint-header-card {
            background: linear-gradient(135deg, #f8fffe 0%, #e8f5e9 100%);
            border-radius: 16px;
            padding: 1.5rem 2rem;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            border: 1px solid rgba(76, 175, 80, 0.1);
            margin-bottom: 1.5rem;
        }

        .complaint-title-wrapper {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }

        .complaint-icon-wrapper {
            width: 48px;
            height: 48px;
            background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
        }

        .complaint-icon-wrapper i {
            color: white;
            font-size: 1.5rem;
        }

        .register-complaint-btn {
            background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important;
            border: none !important;
            border-radius: 10px !important;
            padding: 0.65rem 1.5rem !important;
            font-weight: 600 !important;
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
            transition: all 0.3s ease !important;
        }

        .register-complaint-btn:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
        }

        .register-complaint-btn:active {
            transform: translateY(0) !important;
        }

        @media screen and (max-width: 768px) {
            .complaint-header-card .flex {
                flex-direction: column;
                align-items: flex-start !important;
                gap: 1rem;
            }
            
            .register-complaint-btn {
                width: 100%;
                justify-content: center;
            }
        }
      `}</style>

      {/* Enhanced Header Card */}
      <div className="complaint-header-card">
        <div className="flex align-items-center justify-content-between">
          <div>
            <div className="complaint-title-wrapper">
              <div className="complaint-icon-wrapper">
                <i className="pi pi-users"></i>
              </div>
              <div>
                <h1 className="text-800 font-bold text-3xl m-0" style={{ color: "#10492a" }}>
                  User Traffic Source Audit
                </h1>
                <p className="text-600 m-0 text-sm mt-1">
                  Track, analyze, and manage how users find your platform
                </p>
              </div>
            </div>
          </div>
          <Button
            label="Add New Entry"
            icon="pi pi-plus"
            onClick={() => setShowAddDialog(true)}
            className="register-complaint-btn text-white"
          />
        </div>
      </div>

      {/* Search / Filter Bar */}
      <UserTrafficSourceAuditSearch
        onSearch={handleSearch}
        onReset={handleReset}
        totalRecords={totalRecords}
        setLoading={setIsLoading}
      />

      {/* Data Table */}
      <div className="mt-4">
        <UserTrafficSourceAuditDataTable
          data={data}
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={handlePageChange}
          isLoading={isLoading}
          onRefresh={handleRefresh}
          sortField={sortField}
          sortOrder={sortOrder ?? undefined}
          onSort={(e: any) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
        />
      </div>

      {/* Add Dialog */}
      <UserTrafficSourceAuditAddDialog
        visible={showAddDialog}
        onHide={() => setShowAddDialog(false)}
        onSaved={handleRefresh}
      />
    </div>
  );
};

export default UserTrafficSourceAuditMain;
